import { EventEmitter } from "events";
import { desktopAgentManager } from "../DesktopAgent/DesktopAgentManager";
import { PrinterInfo } from "../DesktopAgent/DesktopAgentTypes";
import {
  PrintReceiptParams,
  PrintTicketsParams,
  PrintThermalTicketsParams,
  PrintResult,
} from "./PrinterServiceTypes";
/**
 * PrinterService - Layer 2: Business Logic
 *
 * Provides high-level printing API for the application.
 * Uses DesktopAgentManager for transport, adds business logic.
 *
 * Events emitted:
 * - 'print-start': When a print job starts
 * - 'print-success': When a print job completes successfully
 * - 'print-error': When a print job fails
 * - 'printers-changed': When the printer list changes
 * - 'printers-loading': When printer fetch starts/ends (payload: boolean)
 */
export class PrinterService extends EventEmitter {
  private cachedPrinters: PrinterInfo[] = [];
  private printerRefreshInterval: NodeJS.Timeout | null = null;
  private fetchingPrintersState = false;

  /**
   * Whether printers are currently being fetched
   */
  get isFetchingPrinters(): boolean {
    return this.fetchingPrintersState;
  }

  /**
   * Set the fetching printers state and emit event
   */
  set isFetchingPrinters(value: boolean) {
    if (this.fetchingPrintersState !== value) {
      this.fetchingPrintersState = value;
      this.emit("printers-loading", value);
    }
  }

  constructor() {
    super();
    this.initialize();
  }

  /**
   * Initialize the service
   */
  private initialize(): void {
    // Listen for manager connection changes
    // Note: Manager may not be initialized yet, but we can still subscribe
    // since it's an EventEmitter - events will fire when manager connects
    desktopAgentManager.on("connected", () => {
      this.refreshPrinters();
    });

    desktopAgentManager.on("disconnected", () => {
      this.cachedPrinters = [];
      this.emit("printers-changed", []);
    });

    // Auto-refresh printers every 30 seconds when connected
    this.printerRefreshInterval = setInterval(() => {
      if (desktopAgentManager.isConnected()) {
        this.refreshPrinters();
      }
    }, 30000);

    // Initial refresh if already connected
    if (desktopAgentManager.isConnected()) {
      this.refreshPrinters();
    }
  }

  /**
   * Print a receipt
   */
  async printReceipt(params: PrintReceiptParams): Promise<PrintResult> {
    this.emit("print-start", { type: "receipt" });

    // Try to ensure connection before checking
    try {
      await desktopAgentManager.ensureConnected();
    } catch {
      // Connection failed, continue to fallback handling below
    }

    // Check if agent is available
    if (!desktopAgentManager.isConnected()) {
      return this.fallbackToBrowserPrint("receipt", params);
    }

    try {
      await desktopAgentManager.send("PRINT_RECEIPT", {
        receiptString: params.receiptString,
        printerName: params.printerName,
        logoUrl: params.logoUrl,
      });

      this.emit("print-success", { type: "receipt", usedFallback: false });

      return {
        success: true,
        usedFallback: false,
      };
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Print failed";
      this.emit("print-error", {
        type: "receipt",
        error: errorMessage,
      });

      // Try fallback on error
      return this.fallbackToBrowserPrint("receipt", params);
    }
  }

  /**
   * Print tickets
   */
  async printTickets(params: PrintTicketsParams): Promise<PrintResult> {
    this.emit("print-start", { type: "tickets" });

    // Try to ensure connection before checking
    try {
      await desktopAgentManager.ensureConnected();
    } catch {
      // Connection failed, continue to fallback handling below
    }

    // Check if agent is available
    if (!desktopAgentManager.isConnected()) {
      return this.fallbackToBrowserPrint("tickets", params);
    }

    try {
      await desktopAgentManager.send("PRINT_TICKETS", {
        ticketItems: params.ticketItems,
        printerName: params.printerName,
        logoUrl: params.logoUrl,
        pdfUrl: params.pdfUrl,
      });

      this.emit("print-success", { type: "tickets", usedFallback: false });

      return {
        success: true,
        usedFallback: false,
      };
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Print failed";
      this.emit("print-error", {
        type: "tickets",
        error: errorMessage,
      });

      // Try fallback on error
      return this.fallbackToBrowserPrint("tickets", params);
    }
  }

  /**
   * Print thermal tickets (BOCA printers)
   */
  async printThermalTickets(
    params: PrintThermalTicketsParams
  ): Promise<PrintResult> {
    this.emit("print-start", { type: "thermal" });

    // Try to ensure connection before checking
    try {
      await desktopAgentManager.ensureConnected();
    } catch {
      // Connection failed, continue to error handling below
    }

    // Check if agent is available
    if (!desktopAgentManager.isConnected()) {
      const error =
        "Desktop Agent not connected. Thermal printing requires the Desktop Agent.";
      this.emit("print-error", { type: "thermal", error });
      return {
        success: false,
        error,
      };
    }

    try {
      await desktopAgentManager.send("PRINT_THERMAL_TICKETS", {
        pdfUrl: params.pdfUrl,
        printerName: params.printerName,
        printOptions: params.printOptions,
      });

      this.emit("print-success", { type: "thermal", usedFallback: false });

      return {
        success: true,
        usedFallback: false,
      };
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Thermal print failed";
      this.emit("print-error", { type: "thermal", error: errorMessage });

      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Get available printers
   */
  async getPrinters(): Promise<PrinterInfo[]> {
    if (!desktopAgentManager.isConnected()) {
      return this.cachedPrinters;
    }

    this.isFetchingPrinters = true;

    try {
      const printers = await desktopAgentManager.send<PrinterInfo[]>(
        "GET_PRINTERS"
      );
      this.cachedPrinters = printers ?? [];
      this.emit("printers-changed", this.cachedPrinters);
      return this.cachedPrinters;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("[PrinterService] Failed to get printers:", error);
      return this.cachedPrinters;
    } finally {
      this.isFetchingPrinters = false;
    }
  }

  /**
   * Refresh printer list
   */
  private async refreshPrinters(): Promise<void> {
    await this.getPrinters();
  }

  /**
   * Get cached printers (synchronous)
   */
  getCachedPrinters(): PrinterInfo[] {
    return this.cachedPrinters;
  }

  /**
   * Check if agent is connected
   */
  // eslint-disable-next-line class-methods-use-this
  isAgentConnected(): boolean {
    return desktopAgentManager.isConnected();
  }

  /**
   * Fallback to browser print dialog
   */
  private fallbackToBrowserPrint(
    type: "receipt" | "tickets",
    params: PrintReceiptParams | PrintTicketsParams
  ): PrintResult {
    // eslint-disable-next-line no-console
    console.warn(`Falling back to browser print for ${type}`);

    // SSR guard
    if (typeof window === "undefined") {
      return {
        success: false,
        error: "Browser print not available during SSR",
        usedFallback: true,
      };
    }

    try {
      // Create a printable version
      const printWindow = window.open("", "_blank");

      if (!printWindow) {
        throw new Error(
          "Failed to open print window. Please check popup blocker."
        );
      }

      let content = "";

      if (
        type === "receipt" &&
        "receiptString" in params &&
        params.receiptString
      ) {
        content = `
          <html>
            <head>
              <title>Receipt</title>
              <style>
                body { font-family: monospace; white-space: pre-wrap; padding: 20px; }
                @media print { body { margin: 0; padding: 10px; } }
              </style>
            </head>
            <body>
              ${params.receiptString}
            </body>
          </html>
        `;
      } else if (
        type === "tickets" &&
        "ticketItems" in params &&
        params.ticketItems
      ) {
        // Simple ticket display
        const ticketsHtml = params.ticketItems
          .map((ticket: Record<string, unknown>) => {
            const eventData = ticket.event_data as Record<string, unknown>;
            const eventName = eventData?.name || "Ticket";
            const barcode =
              ticket.barcode_string || ticket.ticket_credit_string;
            const nameOnTicket = ticket.name_on_ticket
              ? `<p><strong>Name:</strong> ${ticket.name_on_ticket}</p>`
              : "";
            const typeName = ticket.type_name
              ? `<p><strong>Type:</strong> ${ticket.type_name}</p>`
              : "";

            return `
          <div style="page-break-after: always; padding: 20px; border: 1px solid #ccc; margin-bottom: 20px;">
            <h2>${eventName}</h2>
            <p><strong>Barcode:</strong> ${barcode}</p>
            ${nameOnTicket}
            ${typeName}
          </div>
        `;
          })
          .join("");

        content = `
          <html>
            <head>
              <title>Tickets</title>
              <style>
                body { font-family: Arial, sans-serif; padding: 20px; }
                @media print { body { margin: 0; } }
              </style>
            </head>
            <body>
              ${ticketsHtml}
            </body>
          </html>
        `;
      }

      printWindow.document.write(content);
      printWindow.document.close();

      // Trigger print after a short delay
      setTimeout(() => {
        printWindow.print();
        // Don't close automatically - let user close it
      }, 250);

      this.emit("print-success", { type, usedFallback: true });

      return {
        success: true,
        usedFallback: true,
      };
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Browser print failed";
      this.emit("print-error", { type, error: errorMessage });

      return {
        success: false,
        error: errorMessage,
        usedFallback: true,
      };
    }
  }

  /**
   * Cleanup on destroy
   */
  destroy(): void {
    if (this.printerRefreshInterval) {
      clearInterval(this.printerRefreshInterval);
    }
    this.removeAllListeners();
  }
}

// Singleton instance
export const printerService = new PrinterService();
